import React from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';

const MiniCalendar = ({ 
  currentDate, 
  events, 
  view, 
  onDateChange, 
  onNavigate,
  eventTypeColors,
  selectedDay 
}) => {
  // Get events for a specific date
  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventStart = moment(event.start);
      const eventEnd = moment(event.end);
      const checkDate = moment(date);
      
      // For all-day events, check if they overlap with the date
      if (event.allDay) {
        return eventStart.isSameOrBefore(checkDate, 'day') && 
               eventEnd.isSameOrAfter(checkDate, 'day');
      }
      
      // For regular events, check if they start on the date
      return eventStart.isSame(checkDate, 'day');
    });
  };

  // Get unique event types for a date
  const getEventTypesForDate = (date) => {
    const dateEvents = getEventsForDate(date);
    return [...new Set(dateEvents.map(event => event.label))];
  };

  // Handle date click based on current view
  const handleDateClick = (date) => {
    if (view === 'month') {
      // In month view, just update the selected date
      onDateChange(date);
    } else if (view === 'week') {
      // In week view, navigate to the week containing the clicked date
      // and set the selected day
      onNavigate(date);
      onDateChange(date); // This will update the selected day
    } else if (view === 'day') {
      // In day view, navigate to the specific day
      onNavigate(date);
    }
  };

  // Get the value for the calendar based on current view
  const getCalendarValue = () => {
    if (view === 'month') {
      return currentDate;
    } else if (view === 'week') {
      // For week view, show the first day of the current week
      return moment(currentDate).startOf('week').toDate();
    } else if (view === 'day') {
      return currentDate;
    }
    return currentDate;
  };

  // Get the active start date for highlighting
  const getActiveStartDate = () => {
    if (view === 'week') {
      // For week view, highlight the entire week
      return moment(currentDate).startOf('week').toDate();
    }
    return currentDate;
  };

  // Handle month navigation
  const handleMonthNavigation = (action) => {
    let newDate;
    if (action === 'prev') {
      newDate = moment(currentDate).subtract(1, 'month').toDate();
    } else if (action === 'next') {
      newDate = moment(currentDate).add(1, 'month').toDate();
    }
    
    if (newDate) {
      onDateChange(newDate);
    }
  };

  return (
    <Calendar
      onChange={handleDateClick}
      value={getCalendarValue()}
      activeStartDate={getActiveStartDate()}
      locale="en-US"
      formatShortWeekday={(locale, date) => 
        date.toLocaleString(locale, { weekday: 'short' }).slice(0, 1)
      }
      prevLabel={<i className="bi bi-chevron-left"></i>}
      nextLabel={<i className="bi bi-chevron-right"></i>}
      prev2Label={null}
      next2Label={null}
      onActiveStartDateChange={({ action, activeStartDate }) => {
        if (action === 'prev' || action === 'next') {
          handleMonthNavigation(action);
        }
      }}
      navigationLabel={({ date }) => (
        <span className="mini-calendar-navigation-label" style={{ pointerEvents: 'none' }}>
          {date.toLocaleString('default', { month: 'short', year: 'numeric' })}
        </span>
      )}
      tileClassName={({ date }) => {
        const classes = [];
        
        // Add event indicator class
        if (getEventsForDate(date).length > 0) {
          classes.push('mini-calendar-tile--hasEvent');
        }
        
        // Add today class
        if (moment(date).isSame(moment(), 'day')) {
          classes.push('mini-calendar-tile--today');
        }
        
        // Add selected class for week view
        if (view === 'week') {
          const currentWeekStart = moment(currentDate).startOf('week');
          const currentWeekEnd = moment(currentDate).endOf('week');
          const checkDate = moment(date);
          
          if (checkDate.isBetween(currentWeekStart, currentWeekEnd, 'day', '[]')) {
            classes.push('mini-calendar-tile--selected-week');
          }
          
          // Add selected day class for week view
          if (selectedDay && moment(date).isSame(moment(selectedDay), 'day')) {
            classes.push('mini-calendar-tile--selected-day');
          }
        }
        
        // Add selected class for day view
        if (view === 'day' && moment(date).isSame(moment(currentDate), 'day')) {
          classes.push('mini-calendar-tile--selected-day');
        }
        
        return classes.join(' ');
      }}
      tileContent={({ date }) => {
        const eventTypes = getEventTypesForDate(date);
        if (eventTypes.length > 0) {
          // Show maximum of 3 dots
          const dotsToShow = eventTypes.slice(0, 3);
          return (
            <div className="mini-calendar-event-dots">
              {dotsToShow.map((eventType, index) => (
                <span
                  key={index}
                  className="mini-calendar-event-dot"
                  style={{
                    backgroundColor: eventTypeColors[eventType] || eventTypeColors.default
                  }}
                />
              ))}
            </div>
          );
        }
        return null;
      }}
      maxDetail="month"
      minDetail="month"
    />
  );
};

export default MiniCalendar;
